import { useCallback, useEffect, useRef, useState } from 'react';

export type UseLoadingResult<T = void> = Promise<T>;

export const useLoading = <T = void>(
    action: (...args: any[]) => T | UseLoadingResult<T>,
    onError: ((e: any) => void | Promise<void>) | null = null
): [boolean, (...args: Parameters<typeof action>) => Promise<T | undefined>] => {
    const mounted = useRef(true);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    });

    const trigger = useCallback(
        async (...args: Parameters<typeof action>) => {
            setLoading(true);
            try {
                const result = await action(...args);
                if (mounted.current) {
                    setLoading(false);
                }
                return result;
            } catch (e: any) {
                setLoading(false);
                if (onError !== null) {
                    await onError(e);
                } else {
                    console.error(e);
                }
            }
        },
        [action, onError, setLoading]
    );

    return [loading, trigger];
};
